import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  useBreakpointValue,
  Icon,
  Skeleton,
  Tooltip,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  MdBarChart,
  MdInfo,
  MdBusiness,
  MdPerson,
  MdGroups,
} from "react-icons/md";
import FilterComponent from "@/components/FilterBiTable";
import CustomChartComponent from "@/components/BarChartBi";
import KpiBiSection from "@/components/KpiBiSection";
import RankingChartItem from "@/components/RankingBiChartItem";
import ConversionTable from "@/components/ConversionBiTable"; // Import the new component
import { getCefDashboard } from "@/services/dashboardCefBi.service";
import { getCorbansByName } from "@/services/corban.service";
import { getCreditPromoters } from "@/services/creditPromoter.service";
import { getCommercialManagersByName } from "@/services/user.service";
import { getSellerByName } from "@/services/user.service";

const getWeekNumber = (date: string | Date) => {
  const d = typeof date === "string" ? new Date(date) : new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
  );
  return weekNo;
};

export default function DashboardBi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  interface DashboardData {
    countCorbans: number;
    totalConsultas: number;
    propostasPagas: number;
    consultasPorPropostas: number;
    valorLiberado: number;
  }

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [rawData, setRawData] = useState(null);
  const [chartData, setChartData] = useState<
    {
      name: string;
      consultas: number;
      propostasPagas: number;
    }[]
  >([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [corbanOptions, setCorbanOptions] = useState<
    { id: number | undefined; name: string }[]
  >([]);
  const [promoterOptions, setPromoterOptions] = useState<
    { id: number | undefined; name: string }[]
  >([]);
  const [sellerOptions, setSellerOptions] = useState<
    { id: number | undefined; name: string }[]
  >([]);

  const [selectedCorban, setSelectedCorban] = useState(0);
  const [selectedPromoter, setSelectedPromoter] = useState<number | undefined>(
    undefined
  );
  const [selectedSeller, setSelectedSeller] = useState<string | undefined>(
    undefined
  );

  const [selectedManager, setSelectedManager] = useState("");
  const [loadingPromoters, setLoadingPromoters] = useState(true);

  const [promoterPage] = useState(1);
  const [promoterPageSize] = useState(60);
  const [corbanPage] = useState(1);
  const [corbanPageSize] = useState(10);

  const [responsavelOptions, setResponsavelOptions] = useState<
    { id: string | undefined; name: string }[]
  >([]);
  const [loadingResponsavel, setLoadingResponsavel] = useState(false);
  const [loadingSeller, setLoadingSeller] = useState(false);
  const [sellerPage] = useState(1);
  const [sellerPageSize] = useState(10);
  const [responsavelPage] = useState(1);
  const [responsavelPageSize] = useState(10);

  const [loadingCorbans, setLoadingCorbans] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [dataGrouping, setDataGrouping] = useState("daily");
  const [zoomMode] = useState(false);

  const isProcessingRef = useRef(false);

  const headerBgGradient = useColorModeValue(
    "linear(to-r, green.400, blue.500)",
    "linear(to-r, green.600, blue.700)"
  );
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const headerPadding = useBreakpointValue({ base: 4, md: 6 });
  const headerFontSize = useBreakpointValue({ base: "2xl", md: "4xl" });
  const contentPadding = useBreakpointValue({ base: 4, md: 6 });

  const fetchCorbans = async (corbanSearch: string, pagesize: number) => {
    setLoadingCorbans(true);
    try {
      const arrayCorbans = await getCorbansByName(
        corbanSearch,
        corbanPage,
        corbanPageSize + pagesize,
        selectedPromoter
      );

      setCorbanOptions(
        arrayCorbans.data.map((corban) => {
          return {
            id: corban.id,
            name: corban.businessName,
          };
        })
      );
    } catch (error) {
      console.error("Erro ao carregar Corbans:", error);
      setError(
        "Não foi possível carregar a lista de Corbans. Por favor, tente novamente mais tarde."
      );
    } finally {
      setLoadingCorbans(false);
    }
  };

  const fetchPromoters = async (promoterSearch: string, pagesize: number) => {
    setLoadingPromoters(true);
    try {
      const data = await getCreditPromoters(
        promoterPage,
        promoterPageSize + pagesize,
        promoterSearch
      );

      setPromoterOptions(
        data.data
          .map((promoter: any) => ({
            id: promoter.id,
            name: promoter.businessName,
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (error) {
      console.error("Erro ao carregar Promotoras:", error);
      setError(
        "Não foi possível carregar a lista de Promotoras. Por favor, tente novamente mais tarde."
      );
    } finally {
      setLoadingPromoters(false);
    }
  };

  const fetchResponsavel = async (
    responsavelSearch: string,
    pagesize: number
  ) => {
    setLoadingResponsavel(true);
    try {
      // Define the expected type for commercial managers
      interface CommercialManager {
        id: string;
        name: string;
      }

      const response = await getCommercialManagersByName(
        responsavelSearch,
        responsavelPage,
        responsavelPageSize + pagesize
      );

      // Type check and safely map the response
      if (Array.isArray(response)) {
        setResponsavelOptions(
          response.map((manager: CommercialManager) => {
            return {
              id: manager.id,
              name: manager.name,
            };
          })
        );
      } else {
        console.error("Resposta inesperada de getCommercialManagersByName");
        setResponsavelOptions([]);
      }
    } catch (error) {
      console.error("Erro ao carregar Gerentes Comerciais:", error);
      setError(
        "Não foi possível carregar a lista de Gerentes Comerciais. Por favor, tente novamente mais tarde."
      );
      setResponsavelOptions([]);
    } finally {
      setLoadingResponsavel(false);
    }
  };

  const fetchSeller = async (sellerSearch: string, pagesize: number) => {
    setLoadingSeller(true);
    try {
      const response = await getSellerByName(
        sellerSearch,
        sellerPage,
        sellerPageSize + pagesize
      );

      // Check if the response has the expected format

      setSellerOptions(
        response.map((seller: any) => {
          return {
            id: seller.id,
            name: seller.name,
            lastName: seller.lastName,
          };
        })
      );
    } catch (error) {
      console.error("Erro ao carregar Vendedores:", error);
      setError(
        "Não foi possível carregar a lista de Vendedores. Por favor, tente novamente mais tarde."
      );
      setSellerOptions([]);
    } finally {
      setLoadingSeller(false);
    }
  };

  useEffect(() => {
    fetchPromoters("", 0);
    fetchCorbans("", 0);
    fetchResponsavel("", 0);
    fetchSeller("", 0);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffDays = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
      );

      let newGrouping;
      if (diffDays <= 45) {
        newGrouping = "daily";
      } else if (diffDays <= 180) {
        newGrouping = "weekly";
      } else {
        newGrouping = "monthly";
      }

      if (newGrouping !== dataGrouping) {
        setDataGrouping(newGrouping);
      }
    }
  }, [startDate, endDate, dataGrouping]);

  const handleApplyFilters = (
    start: string,
    end: string,
    corban: number,
    promoter: number | undefined,
    responsavel: string,
    seller: string | undefined
  ) => {
    if (!start || !end) return;

    setStartDate(start);
    setEndDate(end);
    setSelectedCorban(corban);
    setSelectedPromoter(promoter);
    setSelectedManager(responsavel);
    setSelectedSeller(seller);

    fetchData(
      start,
      end,
      corban,
      promoter,
      responsavel,
      seller?.toString() || ""
    );
  };

  const fetchData = async (
    startDate: string,
    endDate: string,
    corbanId: number = 0,
    promoterId: number | undefined = 0,
    managerId: string = "",
    sellerId: string = ""
  ) => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getCefDashboard(
        new Date(startDate),
        new Date(endDate),
        corbanId,
        promoterId,
        managerId,
        sellerId
      );

      if (!data) {
        throw new Error("Dados não recebidos da API");
      }

      setDashboardData({
        countCorbans: data.corbanCountTotal || 0,
        totalConsultas: data.totalQuantityBalance || 0,
        propostasPagas: data.totalQuantityProposalPayed || 0,
        consultasPorPropostas: data.queriesDividedByPaidProposals || 0,
        valorLiberado: data.releasedAmount || 0,
      });

      setRawData(data);
      processAndSetChartData(data);

      setLastUpdated(new Date());
    } catch (error) {
      console.error("❌ Erro ao buscar dados do Dashboard BI:", error);
      setError(
        "Não foi possível carregar os dados do dashboard. Por favor, verifique sua conexão e tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const processAndSetChartData = useCallback(
    (data: any) => {
      if (!data || isProcessingRef.current) return;

      isProcessingRef.current = true;

      let formattedData: {
        [key: string]: { consultas: number; propostasPagas: number };
      } = {};

      if (
        data.quantityBalanceGraphic &&
        Array.isArray(data.quantityBalanceGraphic)
      ) {
        data.quantityBalanceGraphic.forEach((item: any) => {
          const { ano, mes, dia, total } = item;

          let key;

          if (dataGrouping === "daily") {
            key = `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
          } else if (dataGrouping === "weekly") {
            const date = new Date(ano, mes - 1, dia);
            const weekNum = getWeekNumber(date);
            key = `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}-${String(weekNum).padStart(2, "0")}`;
          } else {
            key = `${ano}-${String(mes).padStart(2, "0")}`;
          }

          if (!formattedData[key]) {
            formattedData[key] = { consultas: 0, propostasPagas: 0 };
          }
          formattedData[key].consultas += total;
        });
      }

      if (
        data.quantityProposalPayedGraphic &&
        Array.isArray(data.quantityProposalPayedGraphic)
      ) {
        data.quantityProposalPayedGraphic.forEach((item: any) => {
          const { ano, mes, dia, total } = item;

          let key;

          if (dataGrouping === "daily") {
            key = `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
          } else if (dataGrouping === "weekly") {
            const date = new Date(ano, mes - 1, dia);
            const weekNum = getWeekNumber(date);
            key = `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}-${String(weekNum).padStart(2, "0")}`;
          } else {
            key = `${ano}-${String(mes).padStart(2, "0")}`;
          }

          if (!formattedData[key]) {
            formattedData[key] = { consultas: 0, propostasPagas: 0 };
          }
          formattedData[key].propostasPagas += total;
        });
      }

      if (dataGrouping === "weekly") {
        const weeklyData: Record<
          string,
          {
            consultas: number;
            propostasPagas: number;
            firstDay: string;
          }
        > = {};

        Object.keys(formattedData).forEach((key) => {
          const [year, month, week] = key.split("-");
          const weekKey = `${year}-${month}-${week}`;

          if (!weeklyData[weekKey]) {
            weeklyData[weekKey] = {
              consultas: 0,
              propostasPagas: 0,
              firstDay: key,
            };
          }

          weeklyData[weekKey].consultas += formattedData[key].consultas;
          weeklyData[weekKey].propostasPagas +=
            formattedData[key].propostasPagas;
        });

        formattedData = {};
        Object.keys(weeklyData).forEach((weekKey) => {
          formattedData[weeklyData[weekKey].firstDay] = {
            consultas: weeklyData[weekKey].consultas,
            propostasPagas: weeklyData[weekKey].propostasPagas,
          };
        });
      }

      const processedData = Object.keys(formattedData)
        .sort()
        .map((key) => ({
          name: key,
          consultas: formattedData[key].consultas,
          propostasPagas: formattedData[key].propostasPagas,
        }));

      setChartData(processedData);
      isProcessingRef.current = false;
    },
    [dataGrouping]
  );

  useEffect(() => {
    if (rawData && !isProcessingRef.current) {
      processAndSetChartData(rawData);
    }
  }, [dataGrouping, rawData, processAndSetChartData]);

  const handlePromoterSearch = async (
    promoterSearch: string,
    pagesize: number
  ) => {
    await fetchPromoters(promoterSearch, pagesize);
  };

  const handleCorbanSearch = async (corbanSearch: string, pagesize: number) => {
    await fetchCorbans(corbanSearch, pagesize);
  };

  const handleResponsavelSearch = async (
    responsavelSearch: string,
    pagesize: number
  ) => {
    await fetchResponsavel(responsavelSearch, pagesize);
  };

  const handleSellerSearch = async (sellerSearch: string, pagesize: number) => {
    await fetchSeller(sellerSearch, pagesize);
  };
  const handleSellerUpdateOptions = async (options: Array<any>) => {
    await setSellerOptions(options);
  };
  const handleCorbanUpdateOptions = async (options: Array<any>) => {
    await setCorbanOptions(options);
  };
  const handlePromoterUpdateOptions = async (options: Array<any>) => {
    await setPromoterOptions(options);
  };
  const handleResponsavelUpdateOptions = async (options: Array<any>) => {
    await setResponsavelOptions(options);
  };
  const handleRangeSelect = (start: any, end: any) => {
    if (zoomMode && start && end) {
      setStartDate(start);
      setEndDate(end);
      fetchData(
        start,
        end,
        selectedCorban,
        selectedPromoter,
        selectedManager,
        selectedSeller?.toString() || ""
      );
    }
  };

  const handleGroupingChange = (grouping: string) => {
    setDataGrouping(grouping);
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return "";
    return lastUpdated.toLocaleTimeString();
  };

  return (
    <Box w="100%" p={contentPadding}>
      {/* Header Section */}
      <Flex
        flexDirection="column"
        px={headerPadding}
        py={6}
        bgGradient={headerBgGradient}
        borderRadius="xl"
        color="white"
        boxShadow="lg"
      >
        <Flex alignItems="center" mb={4}>
          <Icon as={MdBarChart} boxSize={{ base: 6, md: 8 }} />
          <Text
            fontSize={headerFontSize}
            fontWeight="bold"
            ml={2}
            fontFamily="Lexend Deca"
          >
            Dashboard BI
          </Text>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center">
          {lastUpdated && (
            <Text fontSize="sm" opacity={0.8}>
              Última atualização: {formatLastUpdated()}
            </Text>
          )}
          <Badge
            colorScheme={
              dataGrouping === "daily"
                ? "blue"
                : dataGrouping === "weekly"
                  ? "purple"
                  : "orange"
            }
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
          >
            Visualização{" "}
            {dataGrouping === "daily"
              ? "Diária"
              : dataGrouping === "weekly"
                ? "Semanal"
                : "Mensal"}
          </Badge>
        </Flex>
      </Flex>

      {/* Filter Section */}
      <Box
        mt={4}
        bg={cardBg}
        borderRadius="xl"
        p={5}
        boxShadow="md"
        border="1px solid"
        borderColor={borderColor}
      >
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Flex alignItems="center">
            <Heading size="md">Filtros</Heading>
            <Tooltip label="Selecione o período e outros filtros para visualizar os dados" placement="bottom">
              <Icon as={MdInfo} ml={2} color="gray.500" />
            </Tooltip>
          </Flex>
        </Flex>
        <FilterComponent
          applyFilters={handleApplyFilters}
          applyCorbanSearch={handleCorbanSearch}
          applyPromoterSearch={handlePromoterSearch}
          applyResponsavelSearch={handleResponsavelSearch}
          applySellerSearch={handleSellerSearch}
          corbanOptions={corbanOptions}
          promoterOptions={promoterOptions}
          responsavelOptions={responsavelOptions}
          sellerOptions={sellerOptions}
          isLoadingSeller={loadingSeller}
          isLoading={loadingCorbans}
          isLoadingPromoter={loadingPromoters}
          isLoadingResponsavel={loadingResponsavel}
          updateSellerOptions={handleSellerUpdateOptions}
          updateCorbanOptions={handleCorbanUpdateOptions}
          updatePromoterOptions={handlePromoterUpdateOptions}
          updateResponsavelOptions={handleResponsavelUpdateOptions}
        />
      </Box>

      {/* Error Message */}
      {error && (
        <Alert status="error" mt={4} borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Erro!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Content Section */}
      <Box w="100%" mt={4}>
        {/* KPI Section */}
        {loading ? (
          <Skeleton height="120px" borderRadius="lg" mb={4} />
        ) : (
          dashboardData && (
            <KpiBiSection
              totalCorbans={dashboardData.countCorbans}
              totalConsultas={dashboardData.totalConsultas}
              propostasPagas={dashboardData.propostasPagas}
              consultasPorPropostas={dashboardData.consultasPorPropostas}
              valorLiberado={dashboardData.valorLiberado}
            />
          )
        )}

        {/* Chart Section */}
        <Box mt={4}>
          {loading ? (
            <Flex
              justifyContent="center"
              alignItems="center"
              height="350px"
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
            >
              <Flex direction="column" align="center">
                <Spinner size="xl" color="blue.500" mb={4} />
                <Text>Carregando dados...</Text>
              </Flex>
            </Flex>
          ) : chartData.length > 0 ? (
            <CustomChartComponent
              data={chartData}
              startDate={startDate}
              endDate={endDate}
              onRangeSelect={handleRangeSelect}
              dataGrouping={dataGrouping}
              onGroupingChange={handleGroupingChange}
            />
          ) : (
            <Flex
              justifyContent="center"
              alignItems="center"
              height="350px"
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              border="1px dashed"
              borderColor={borderColor}
            >
              <Text color="gray.500">
                {startDate && endDate
                  ? "Nenhum dado disponível para o período selecionado"
                  : "Selecione um período para visualizar os dados"}
              </Text>
            </Flex>
          )}
        </Box>

        {/* Conversion Analysis and Ranking Charts Section - 1/3 + 2/3 Split Layout */}
        {!loading && rawData && (
          <Flex
            mt={6}
            direction={{ base: "column", md: "row" }}
            gap={6}
            w="100%"
          >
            {/* Conversion Analysis - 1/3 width */}
            <Box
              flex={{ base: "1", md: "1" }}
              w={{ base: "100%", md: "33.333%" }}
            >
              <ConversionTable dashboardData={rawData} isLoading={loading} />
            </Box>

            {/* Ranking Charts - 2/3 width */}
            <Box
              flex={{ base: "1", md: "2" }}
              w={{ base: "100%", md: "66.666%" }}
              bg={cardBg}
              borderRadius="xl"
              boxShadow="lg"
              p={{ base: 4, md: 5 }}
              border="1px solid"
              borderColor={borderColor}
            >
              <Tabs variant="enclosed-colored" colorScheme="blue" isFitted>
                <TabList mb="1em">
                  <Tab>
                    <Flex align="center">
                      <Icon as={MdBusiness} mr={2} />
                      <Text>Corban</Text>
                    </Flex>
                  </Tab>
                  <Tab>
                    <Flex align="center">
                      <Icon as={MdGroups} mr={2} />
                      <Text>Promotora</Text>
                    </Flex>
                  </Tab>
                  <Tab>
                    <Flex align="center">
                      <Icon as={MdPerson} mr={2} />
                      <Text>Vendedor</Text>
                    </Flex>
                  </Tab>
                </TabList>
                <TabPanels>
                  {/* Corban Tab */}
                  <TabPanel px={0}>
                    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8}>
                      <Box>
                        <Heading size="md" mb={4}>
                          Total Liberado por Corban
                        </Heading>
                        <RankingChartItem
                          title="Total Liberado por Corban"
                          entityType="corban"
                          dataType="liberado"
                          dashboardData={rawData}
                        />
                      </Box>
                      <Box>
                        <Heading size="md" mb={4}>
                          Total Consultado por Corban
                        </Heading>
                        <RankingChartItem
                          title="Total Consultado por Corban"
                          entityType="corban"
                          dataType="consulta"
                          dashboardData={rawData}
                        />
                      </Box>
                    </SimpleGrid>
                  </TabPanel>

                  {/* Promotora Tab */}
                  <TabPanel px={0}>
                    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8}>
                      <Box>
                        <Heading size="md" mb={4}>
                          Total Liberado por Promotora
                        </Heading>
                        <RankingChartItem
                          title="Total Liberado por Promotora"
                          entityType="promotora"
                          dataType="liberado"
                          dashboardData={rawData}
                        />
                      </Box>
                      <Box>
                        <Heading size="md" mb={4}>
                          Total Consultado por Promotora
                        </Heading>
                        <RankingChartItem
                          title="Total Consultado por Promotora"
                          entityType="promotora"
                          dataType="consulta"
                          dashboardData={rawData}
                        />
                      </Box>
                    </SimpleGrid>
                  </TabPanel>

                  {/* Vendedor Tab */}
                  <TabPanel px={0}>
                    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8}>
                      <Box>
                        <Heading size="md" mb={4}>
                          Total Liberado por Vendedor
                        </Heading>
                        <RankingChartItem
                          title="Total Liberado por Vendedor"
                          entityType="vendedor"
                          dataType="liberado"
                          dashboardData={rawData}
                        />
                      </Box>
                      <Box>
                        <Heading size="md" mb={4}>
                          Total Consultado por Vendedor
                        </Heading>
                        <RankingChartItem
                          title="Total Consultado por Vendedor"
                          entityType="vendedor"
                          dataType="consulta"
                          dashboardData={rawData}
                        />
                      </Box>
                    </SimpleGrid>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Flex>
        )}
      </Box>
    </Box>
  );
}
